import type {
  AccessRecordPublic,
  ChecklistAction,
  ChecklistItemPublic,
  LifecycleChecklistPublic,
} from '@accounts/shared';

/*
 * THE LIFECYCLE IT EMAIL OVER A CHOSEN SET OF ITEMS. A line-for-line copy of the server's
 * `renderItEmail` (server/src/modules/lifecycle/service.ts), which renders every open item.
 * The checklist dialog uses the server's text whenever the email tracks every item it names;
 * this copy is used only where some of them are already with IT, so the text names exactly
 * the items the email tracks and keeps each item's lifecycle action (remove the account,
 * remove from the VM profile, rotate the shared password, remove from the shared mailbox).
 * Keep the two in step.
 */

function systemLabel(record: AccessRecordPublic): string {
  return [...record.path.map((step) => step.name), record.catalogItem.name].join(' > ');
}

function names(people: readonly { name: string }[]): string {
  return people.map((person) => person.name).join(', ');
}

function quoted(login: string | null): string {
  return login === null ? 'their login' : `"${login}"`;
}

function emailLine(action: ChecklistAction, record: AccessRecordPublic, personName: string): string {
  const login = quoted(record.login);
  const others = names(record.sharedWith);
  const caseLogin = record.caseLogin === null ? '' : ` Case login: "${record.caseLogin}".`;
  switch (action) {
    case 'set_up': {
      const create = record.login === null ? 'Create a login.' : `Create the login ${login}.`;
      const password =
        record.passwordHandling === 'given_raw'
          ? 'Password: Given raw (give it to them).'
          : 'Password: Managed (save it in their VM profile; do not tell them).';
      return `${create}${caseLogin} ${password}`;
    }
    case 'remove': {
      const count = record.sharedWith.length;
      return count === 0
        ? `Remove the account ${login}.${caseLogin}`
        : `Remove the account ${login}.${caseLogin} Warning: ${String(count)} other ${count === 1 ? 'person uses' : 'people use'} this login and will lose it: ${others}.`;
    }
    case 'remove_vm_access':
      return `Remove ${login} from ${personName}'s VM profile.${caseLogin} Do not delete the account: ${others} still use it.`;
    case 'rotate_shared_password':
      return `Rotate the shared password for ${login} and give the new one to ${others}.${caseLogin} Do not delete the account.`;
    case 'remove_from_shared_mailbox':
      return `Remove ${personName} from the shared mailbox ${login}.${caseLogin} The mailbox stays for ${others}.`;
    case 'app_deactivation':
      return '';
  }
}

function section(
  heading: 'SET UP.' | 'REMOVE.',
  items: readonly ChecklistItemPublic[],
  personName: string,
): string[] {
  if (items.length === 0) return [];
  const bySystem = new Map<string, string[]>();
  for (const item of items) {
    if (item.record === null) continue;
    const label = systemLabel(item.record);
    const lines = bySystem.get(label) ?? [];
    lines.push(`- ${emailLine(item.action, item.record, personName)}`);
    bySystem.set(label, lines);
  }
  const out: string[] = [heading];
  for (const label of [...bySystem.keys()].sort((a, b) => a.localeCompare(b))) {
    out.push(label, ...(bySystem.get(label) ?? []), '');
  }
  return out;
}

/** The email text over `items` (each an open item with a record), grouped as the server does. */
export function renderChecklistItEmail(
  checklist: LifecycleChecklistPublic,
  items: readonly ChecklistItemPublic[],
): string {
  const open = items.filter((item) => item.record !== null);
  const setUp = open.filter((item) => item.action === 'set_up');
  const remove = open.filter((item) => item.action !== 'set_up');
  const apps = checklist.items.filter(
    (item) => item.action === 'app_deactivation' && item.status === 'open' && item.retiredAt === null,
  );
  const person = checklist.user;

  const subject =
    setUp.length > 0 && remove.length > 0
      ? 'Set up and remove access'
      : setUp.length > 0
        ? 'Set up access'
        : 'Remove access';
  const intro =
    checklist.kind === 'onboarding'
      ? `${person.name} (${person.email}) is joining. Please set up the access below and reply when it is done.`
      : `${person.name} (${person.email}) is leaving. Please remove the access below and reply when it is done. Where a login is shared, do exactly what the line says: rotate or remove their access, and delete a shared account only where the line says "Remove the account".`;

  const lines = [
    `Subject: ${subject} for ${person.name}`,
    '',
    'Hello IT,',
    '',
    intro,
    '',
    ...section('SET UP.', setUp, person.name),
    ...section('REMOVE.', remove, person.name),
  ];
  if (apps.length > 0) {
    lines.push(
      `Suite applications (${String(apps.length)}) are deactivated by Accounts. Nothing to do for them.`,
      '',
    );
  }
  lines.push('Thank you.');
  return `${lines.join('\n')}\n`;
}
